import { Add, ArrowForward } from '@mui/icons-material';
import { moduleNavigation } from '@/config/navigation.custom';
import type {
  ModuleNavigationConfig,
  NavigationCategory,
  NavigationMenuItem,
  QuickAction,
} from '@/config/navigation.generated';
import { moduleEnableFeature } from '@/lib/moduleFeatures';
import type { SlashCommand } from './types';

/**
 * Build "Go to X" commands from every navigable menu item in every module.
 * The command id is stable (`nav.<module>.<itemId>`) so MRU works across reloads.
 */
function buildNavigationCommands(
  module: ModuleNavigationConfig,
  category: NavigationCategory,
  item: NavigationMenuItem,
): SlashCommand {
  return {
    id: `nav.${module.module.toLowerCase()}.${item.id}`,
    title: {
      key: 'slashCommand.nav.goto',
      value: `Go to ${item.label}`,
      args: {
        view: item.label,
      },
      // The view name is resolved through the same Menu:* key the sidebar uses, so the palette
      // reads and matches in the active language rather than in the DSL's English label.
      ...(item.labelKey ? { argKeys: { view: item.labelKey } } : {}),
    },
    subtitle: {
      key: module.labelKey,
      value: module.label,
    },
    group: 'navigation',
    keywords: [
      module.label,
      module.module,
      category.label,
      item.label,
      item.entity ?? '',
    ].filter(Boolean),
    localizedKeywordsKey: `slashCommand.keywords.${`nav.${module.module.toLowerCase()}.${item.id}`}`,
    icon: item.icon ?? ArrowForward,
    permission: item.permission,
    feature: moduleEnableFeature(module.module),
    run: ({ navigate, closeDialog }) => {
      closeDialog();
      navigate(item.path);
    },
  };
}

/**
 * Build "New X" create commands from every QuickAction entry with `action: 'create'`.
 */
function buildCreateCommand(module: ModuleNavigationConfig, qa: QuickAction): SlashCommand | null {
  if (qa.action !== 'create' || !qa.path) return null;

  return {
    id: `create.${module.module.toLowerCase()}.${qa.id}`,
    title: {
      key: qa.labelKey,
      value: qa.label,
    },
    subtitle: {
      key: module.labelKey,
      value: module.label,
    },
    group: 'create',
    keywords: [qa.label, module.label, module.module, 'new', 'create'],
    localizedKeywordsKey: `slashCommand.keywords.create.${module.module.toLowerCase()}.${qa.id}`,
    icon: qa.icon ?? Add,
    permission: qa.permission,
    feature: moduleEnableFeature(module.module),
    run: ({ navigate, closeDialog }) => {
      closeDialog();
      navigate(qa.path!);
    },
  };
}

/**
 * Derive the full list of navigation + create commands from the module navigation config.
 * Pure function of config, so safe to compute once per module load.
 */
export function buildNavCommands(): SlashCommand[] {
  const commands: SlashCommand[] = [];

  for (const module of moduleNavigation) {
    for (const qa of module.quickActions) {
      const cmd = buildCreateCommand(module, qa);
      if (cmd) commands.push(cmd);
    }

    for (const category of module.categories) {
      for (const item of category.items) {
        commands.push(buildNavigationCommands(module, category, item));
      }
    }
  }

  return commands;
}
